import { useEffect, useRef, useState } from 'react'
import styles from './TheBridgeBelief.module.css'

// The three-part progression of the belief
const beliefs = [
  {
    statement: 'Vibe coding works.',
    elaboration:
      'For a single person, at speed, with full context in their head, it works remarkably well.',
  },
  {
    statement: 'The problem arrives when the codebase has history.',
    elaboration:
      "When three people made three different decisions that were all correct at the time. When the next edit has to fit a system it didn't create.",
  },
  {
    statement: 'Most AI tooling optimizes for the moment of writing.',
    elaboration: 'We optimize for the moment someone else has to read it.',
  },
]

const TheBridgeBelief = () => {
  const sectionRef = useRef<HTMLElement>(null)
  const beliefRefs = useRef<(HTMLDivElement | null)[]>([])
  const closingRef = useRef<HTMLParagraphElement>(null)
  const [visibleBeliefs, setVisibleBeliefs] = useState<boolean[]>(
    new Array(beliefs.length).fill(false)
  )
  const [headerVisible, setHeaderVisible] = useState(false)
  const [closingVisible, setClosingVisible] = useState(false)
  const [scrollProgress, setScrollProgress] = useState(0)

  useEffect(() => {
    const observers: IntersectionObserver[] = []

    // Header observer
    const headerObserver = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setHeaderVisible(true) },
      { threshold: 0.3 }
    )
    if (sectionRef.current) headerObserver.observe(sectionRef.current)
    observers.push(headerObserver)

    // Closing observer
    const closingObserver = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setClosingVisible(true) },
      { threshold: 0.3 }
    )
    if (closingRef.current) closingObserver.observe(closingRef.current)
    observers.push(closingObserver)

    // Per-belief observers — stagger reveal as each enters viewport
    beliefRefs.current.forEach((el, i) => {
      if (!el) return
      const obs = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting) {
            setVisibleBeliefs(prev => {
              const next = [...prev]
              next[i] = true
              return next
            })
          }
        },
        { threshold: 0.25 }
      )
      obs.observe(el)
      observers.push(obs)
    })

    // Scroll for ambient parallax
    const handleScroll = () => {
      if (!sectionRef.current) return
      const rect = sectionRef.current.getBoundingClientRect()
      const progress = Math.max(0, Math.min(1,
        (window.innerHeight - rect.top) / (window.innerHeight + rect.height)
      ))
      setScrollProgress(progress)
    }
    window.addEventListener('scroll', handleScroll, { passive: true })
    handleScroll()

    return () => {
      observers.forEach(o => o.disconnect())
      window.removeEventListener('scroll', handleScroll)
    }
  }, [])

  return (
    <section ref={sectionRef} className={styles.section}>

      {/* Ambient background — diagonal scan texture */}
      <div className={styles.scanTexture} />

      {/* Drifting glow orb */}
      <div
        className={styles.orb}
        style={{ transform: `translate(-50%, calc(-50% + ${scrollProgress * 60}px))` }}
      />

      <div className="container">
        <div className={styles.layout}>

          {/* Sticky label column */}
          <div className={styles.labelColumn}>
            <div
              className={styles.sectionTag}
              style={{
                opacity: headerVisible ? 1 : 0,
                transform: headerVisible ? 'translateY(0)' : 'translateY(16px)',
                transition: 'opacity 0.8s ease, transform 0.8s ease',
              }}
            >
              <span className={styles.tagNumber}>05</span>
              <span className={styles.tagLine} />
              <span className={styles.tagText}>The Bridge Belief</span>
            </div>
          </div>

          {/* Content column */}
          <div className={styles.contentColumn}>

            {/* Preamble */}
            <p
              className={styles.preamble}
              style={{
                opacity: headerVisible ? 1 : 0,
                transform: headerVisible ? 'translateY(0)' : 'translateY(20px)',
                transition: 'opacity 0.9s ease 0.15s, transform 0.9s ease 0.15s',
              }}
            >
              It understands the difference between building alone and building together.
            </p>

            {/* Belief rows */}
            <div className={styles.beliefList}>
              {beliefs.map((belief, i) => (
                <div
                  key={i}
                  ref={el => beliefRefs.current[i] = el}
                  className={styles.beliefRow}
                  style={{
                    opacity: visibleBeliefs[i] ? 1 : 0,
                    transform: visibleBeliefs[i] ? 'translateX(0)' : 'translateX(-24px)',
                    transition: `opacity 0.7s ease ${i * 0.04}s, transform 0.7s ease ${i * 0.04}s`,
                  }}
                >
                  {/* Strike-bar that animates in */}
                  <div
                    className={styles.strikeBar}
                    style={{
                      width: visibleBeliefs[i] ? '100%' : '0%',
                      transition: `width 0.6s cubic-bezier(0.22, 1, 0.36, 1) ${0.25 + i * 0.05}s`,
                    }}
                  />

                  <div className={styles.beliefInner}>
                    <p className={styles.beliefStatement}>{belief.statement}</p>
                    <p className={styles.beliefElaboration}>{belief.elaboration}</p>
                  </div>
                </div>
              ))}
            </div>

            {/* Closing line */}
            <p
              ref={closingRef}
              className={styles.closing}
              style={{
                opacity: closingVisible ? 1 : 0,
                transform: closingVisible ? 'translateY(0)' : 'translateY(16px)',
                transition: 'opacity 0.9s ease 0.3s, transform 0.9s ease 0.3s',
              }}
            >
              Box Editor was built for what comes{' '}
              <em className={styles.closingEmphasis}>after</em>{' '}
              the prototype. When the solo build becomes a team. When the thing
              you made quickly needs to be maintained carefully.
            </p>

          </div>
        </div>
      </div>
    </section>
  )
}

export default TheBridgeBelief
